import React from 'react';
import { Chip, Stack, Typography } from '@mui/material';
import { CampaignStep } from '../../../../support/campaigns';

/**
 * "Where it's used" support for the campaign's Reference Objects (outcomes, question pool, personas,
 * team roles). A reference object is a reusable attribute defined once at the campaign level; steps
 * then *implement* it by citing it in their `metadata`. This module scans every step once and indexes
 * those citations so each reference editor can show which steps consume a given row — and so an author
 * knows what a change or a delete will affect before they make it.
 *
 * The citation seams (all on `CampaignStep.metadata`, written by the step builder):
 *   • personas   → `workshopPrepConfig.personas.selected[].ref`   (persona id-as-string, or label)
 *   • team roles → `workshopPrepConfig.team.roleRefs[]`           (role name)
 *   • questions  → `questionRefs[].{category,key}`                (a content-block question)
 *
 * A pool question (`CampaignQuestion`) links back to a content block through its
 * `metadata.source.{category,key}` (seeded by migration 0175), so its usage is the union of the steps
 * citing that content block. Outcomes are referenced indirectly (by the pool questions that explore
 * them), so their usage is derived one hop further, in the Outcomes editor itself.
 */

export interface ReferenceUsageIndex {
  /** Steps citing a persona ref (`personaSelectionRef`). */
  personas: Map<string, CampaignStep[]>;
  /** Steps citing a team-role by name. */
  teamRoles: Map<string, CampaignStep[]>;
  /** Steps citing a content-block question by `${category}:${key}`. */
  questionRefs: Map<string, CampaignStep[]>;
}

const qKey = (category: string, key: string): string => `${category}:${key}`;

function push<K>(map: Map<K, CampaignStep[]>, mapKey: K, step: CampaignStep): void {
  const list = map.get(mapKey);
  if (list) {
    if (!list.includes(step)) list.push(step);
  } else {
    map.set(mapKey, [step]);
  }
}

/** Build the usage index from a campaign's steps. Cheap; memoize on the step list in the caller. */
export function buildReferenceUsage(steps: CampaignStep[]): ReferenceUsageIndex {
  const index: ReferenceUsageIndex = {
    personas: new Map(),
    teamRoles: new Map(),
    questionRefs: new Map(),
  };

  for (const step of steps) {
    const meta = (step.metadata ?? {}) as {
      questionRefs?: Array<{ category?: string; key?: string }>;
      workshopPrepConfig?: {
        personas?: { selected?: Array<{ ref?: string }> };
        team?: { roleRefs?: string[] };
      };
    };

    for (const ref of meta.questionRefs ?? []) {
      if (ref?.category && ref?.key) push(index.questionRefs, qKey(ref.category, ref.key), step);
    }
    for (const sel of meta.workshopPrepConfig?.personas?.selected ?? []) {
      if (sel?.ref) push(index.personas, sel.ref, step);
    }
    for (const name of meta.workshopPrepConfig?.team?.roleRefs ?? []) {
      if (name) push(index.teamRoles, name, step);
    }
  }

  return index;
}

/** Steps citing the content-block question a pool row was projected from (via `metadata.source`). */
export function questionUsage(
  index: ReferenceUsageIndex,
  source: { category?: string; key?: string } | undefined,
): CampaignStep[] {
  if (!source?.category || !source?.key) return [];
  return index.questionRefs.get(qKey(source.category, source.key)) ?? [];
}

/**
 * A compact "Used in" line for a reference-object editor row. Renders the citing steps as chips, or a
 * quiet "not referenced yet" note so an author can tell an orphaned row from a load-bearing one.
 */
export function WhereUsed(props: {
  steps: CampaignStep[];
  /** What the reference is, for the empty-state note (e.g. "outcome", "persona"). */
  noun?: string;
}): JSX.Element {
  const { steps, noun = 'reference' } = props;
  if (steps.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        Not used by any step yet — this {noun} is safe to change or remove.
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color="text.secondary">
        Used in {steps.length} step{steps.length === 1 ? '' : 's'}:
      </Typography>
      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
        {steps.map((s) => (
          <Chip key={s.id} size="small" variant="outlined" label={s.name || `Step ${s.id}`} />
        ))}
      </Stack>
    </Stack>
  );
}
